import {isReadOSTurnTerminalEvent,type CodexNotification} from '@reados/contracts';

type Value=Record<string,unknown>;
const object=(value:unknown):Value=>value&&typeof value==='object'&&!Array.isArray(value)?value as Value:{};
const identifier=(value:unknown)=>typeof value==='string'&&value.length>0?value:undefined;
const milliseconds=(value:unknown)=>typeof value==='number'&&Number.isFinite(value)&&value>=0?value:undefined;
const timestamp=(value:unknown)=>typeof value==='number'&&Number.isFinite(value)&&value>0?value:undefined;
export type TurnStatus='inProgress'|'completed'|'interrupted'|'failed';
export interface TurnLifecycle {
  id:string;
  threadId:string;
  status:TurnStatus;
  startedAtMilliseconds?:number;
  completedAtMilliseconds?:number;
  durationMilliseconds?:number;
}
export const turnIdentity=(threadId:unknown,turnId:unknown)=>JSON.stringify([threadId,turnId]);

/** Exact native identities join lifecycle presentation. Application interruption
 * receipts describe an observed process boundary, never a fabricated native
 * completion. Neither input events nor native item/history payloads are changed.
 */
export function turnLifecycles(events:CodexNotification[]):Map<string,TurnLifecycle> {
  const turns=new Map<string,TurnLifecycle>(),terminalAuthority=new Map<string,number>();
  for(const event of events){
    const params=object(event.params),turn=object(params.turn);
    const receipt=event.method==='reados/turnTerminal';
    if(receipt&&!isReadOSTurnTerminalEvent(event))continue;
    if(!receipt&&!['turn/started','turn/completed','turn/interrupted','turn/failed'].includes(event.method))continue;
    const threadId=identifier(params.threadId),id=identifier(receipt?params.turnId:turn.id??params.turnId);
    if(!threadId||!id)continue;
    const key=turnIdentity(threadId,id),previous=turns.get(key);
    const startedAt=timestamp(turn.startedAt);
    const startedAtMilliseconds=startedAt===undefined?previous?.startedAtMilliseconds:startedAt*1000;
    const status:TurnStatus|undefined=receipt
      ? params.status==='interrupted'||params.status==='failed'?params.status:undefined
      : event.method==='turn/started'?'inProgress'
      : turn.status==='interrupted'||turn.status==='failed'||turn.status==='completed'?turn.status
      : event.method==='turn/interrupted'?'interrupted':event.method==='turn/failed'?'failed':'completed';
    if(!status)continue;
    // A delayed/replayed start cannot reopen a terminal turn. Real native
    // terminal data takes precedence over an application observation receipt.
    const authority=status==='inProgress'?0:receipt?1:2;
    if((terminalAuthority.get(key)??0)>authority){
      const recoveredStart=previous?.startedAtMilliseconds??startedAtMilliseconds;
      const duration=previous?.durationMilliseconds
        ??(recoveredStart!==undefined&&previous?.completedAtMilliseconds!==undefined&&previous.completedAtMilliseconds>=recoveredStart
          ?previous.completedAtMilliseconds-recoveredStart:undefined);
      turns.set(key,{...previous!,...(recoveredStart===undefined?{}:{startedAtMilliseconds:recoveredStart}),
        ...(duration===undefined?{}:{durationMilliseconds:duration})});
      continue;
    }
    const nativeEnd=timestamp(turn.completedAt);
    // Restart and legacy state records only observe that work has stopped.
    // Their wall-clock timestamps do not prove the actual execution end; only
    // a current runtime-retirement receipt supplies that boundary here.
    const completedAtMilliseconds=receipt?params.reason==='runtime-retired'?timestamp(params.completedAtMilliseconds):undefined
      :nativeEnd===undefined?previous?.completedAtMilliseconds:nativeEnd*1000;
    const nativeDuration=milliseconds(turn.durationMs);
    const durationMilliseconds=status==='inProgress'?undefined:nativeDuration
      ??(startedAtMilliseconds!==undefined&&completedAtMilliseconds!==undefined&&completedAtMilliseconds>=startedAtMilliseconds
        ?completedAtMilliseconds-startedAtMilliseconds:undefined);
    turns.set(key,{id,threadId,status,
      ...(startedAtMilliseconds===undefined?{}:{startedAtMilliseconds}),
      ...(completedAtMilliseconds===undefined?{}:{completedAtMilliseconds}),
      ...(durationMilliseconds===undefined?{}:{durationMilliseconds})});
    if(authority)terminalAuthority.set(key,authority);
  }
  return turns;
}
